// src/components/issues/EnvoiBE.tsx — SCE Issues
// « Envoyer au BE » depuis la fiche problème (20/10, coordinateurs du métier,
// suppléants, admin). Maquette validée : https://claude.ai/artifact/D9KRDkXqNmEYL9aQDp7Jwd
//
// Envoi EXPRESS : les personnes du BE du métier déjà choisies (config/be.config),
// un mot facultatif (chacun reçoit selon ses réglages Mail / Teams, 21/10) — le résumé est choisi par l'appli
// (utils/envoiBE : commentaires utiles). « Voir et ajuster » montre ce qui
// part et pourquoi, pour cocher ou décocher avant d'envoyer. Les photos ne
// partent plus (22/10) : le message en donne le nombre, on les voit sur la fiche.
//
// Une case « Me mettre en copie du mail » (cochée d'office, 23/10) dit si le
// coordinateur veut aussi le mail : le BE lui répond dans tous les cas, la copie
// n'est qu'un exemplaire pour lui.
//
// L'appli écrit la demande dans SCE_Envois ; le flow 6 envoie la carte Teams et / ou
// le mail, selon les réglages de chaque destinataire. La fiche trace ensuite l'envoi dans l'activité
// (`onEnvoye`). Même fenêtre que la NC-S (FenetreQualite) : centrée au bureau,
// par le bas au téléphone ; son fond ne ferme rien, Échap la ferme.

import { useEffect, useMemo, useState, type CSSProperties, type JSX, type KeyboardEvent, type ReactNode } from 'react'
import { CheckCircle2, ChevronLeft, ChevronRight, Paperclip, Send, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { ChoixPersonne } from '@/components/personnes/ChoixPersonne'
import { PastillePersonne } from '@/components/personnes/PastillePersonne'
import { useAuth } from '@/auth/useAuth'
import { identiteDuCompte } from '@/auth/identiteCompte'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useEstBureau } from '@/hooks/useMediaQuery'
import { useConfirmerEcriture } from '@/features/vue-role/contexte'
import { deposerEnvoi, lireDestinatairesFrequents, messageErreurEnvoi, noterDestinataires } from '@/api/envoisBE.api'
import { getBE } from '@/config/be.config'
import { pilotesDuMetier } from '@/config/coordinateurs.config'
import { rolesCharges } from '@/config/registreRoles'
import { adresseProposable, estCompteInvite } from '@/utils/roles'
import { METIERS_LABELS_COURT } from '@/config/metiers.config'
import {
  choisirCommentaires,
  habituesEnvoi,
  libelleDestination,
  LIBELLES_RAISON,
  LONGUEUR_EXTRAIT,
  texteEvenementEnvoi,
  tronquer,
  type Destinataire,
  type DestinataireFrequent,
} from '@/utils/envoiBE'
import type { PhotoFiche } from '@/utils/rattachementPhotos'
import { A_QUALIFIER, type Commentaire, type Equipement, type Issue } from '@/types/issue.types'
import { Fenetre, STYLE_AIDE, STYLE_CHAMP, STYLE_ETIQUETTE, STYLE_SECTION, type Disposition } from './FenetreQualite'

export interface EnvoiBEProps {
  issue: Issue
  equipement: Equipement | null | undefined
  commentaires: readonly Commentaire[]
  /** Le bloc Photos de la fiche : le message n'en donne que le nombre. */
  photos: readonly PhotoFiche[]
  /** Identifiants des commentaires qui portent une photo. */
  avecPhoto: ReadonlySet<string>
  /** Demande écrite : la fiche trace l'envoi dans l'activité. */
  onEnvoye: (texteEvenement: string, message: string) => Promise<void>
  onClose: () => void
}

const memeAdresse = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase()

export function EnvoiBE({ issue, equipement, commentaires, photos, avecPhoto, onEnvoye, onClose }: EnvoiBEProps): JSX.Element {
  const { nomReel, user } = useCurrentUser()
  const { account } = useAuth()
  const estBureau = useEstBureau()
  const confirmerEcriture = useConfirmerEcriture()
  const disposition: Disposition = estBureau ? 'centre' : 'bas'
  const nomBE = `BE ${METIERS_LABELS_COURT[issue.metier]}`
  const be = useMemo(() => getBE(issue.metier), [issue.metier])

  // ─── Ce qui part ───
  const [destinataires, setDestinataires] = useState<Destinataire[]>(be)
  const [beDeplie, setBeDeplie] = useState(false)
  const [ajout, setAjout] = useState(be.length === 0)
  const [note, setNote] = useState('')
  // Cochée d'office : c'était déjà le comportement avant la case (le flow 6 mettait le coordinateur en Cc).
  const [copieExpediteur, setCopieExpediteur] = useState(true)
  // Le compte réel (bascule de rôle comprise) : c'est lui qui envoie, ses habitudes qu'on retient.
  const upnReel = identiteDuCompte(account)?.upn ?? user?.upn ?? ''
  const [frequents, setFrequents] = useState<DestinataireFrequent[]>([])
  useEffect(() => {
    let vivant = true
    if (upnReel) void lireDestinatairesFrequents(upnReel).then((f) => vivant && setFrequents(f))
    return () => {
      vivant = false
    }
  }, [upnReel])

  const candidats = useMemo(
    () =>
      choisirCommentaires(commentaires, {
        coordinateurs: pilotesDuMetier(issue.metier).map((c) => c.nom),
        declarant: issue.declarantNom || issue.declarant,
        avecPhoto,
      }),
    [commentaires, issue.metier, issue.declarantNom, issue.declarant, avecPhoto],
  )
  const nbPhotos = photos.filter((p) => p.pj.isImage).length
  const [commentairesGardes, setCommentairesGardes] = useState<ReadonlySet<string>>(
    () => new Set(candidats.filter((c) => c.garde).map((c) => c.commentaire.id)),
  )
  const retenus = candidats.filter((c) => commentairesGardes.has(c.commentaire.id)).map((c) => c.commentaire)

  const [vue, setVue] = useState<'express' | 'ajuster'>('express')
  const [envoi, setEnvoi] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [envoye, setEnvoye] = useState<string | null>(null)

  const destination = libelleDestination(issue.metier, destinataires, be)
  const manque = destinataires.length === 0 ? 'Choisissez au moins une personne.' : null
  // Un externe ne reçoit que le mail (Teams n'écrit pas à un compte invité) : on le dit.
  const avecExternes = destinataires.some((d) => estCompteInvite(d.upn))

  const basculer = (ensemble: ReadonlySet<string>, id: string): Set<string> => {
    const s = new Set(ensemble)
    if (s.has(id)) s.delete(id)
    else s.add(id)
    return s
  }
  const retirer = (upn: string): void => setDestinataires((ds) => ds.filter((d) => !memeAdresse(d.upn, upn)))
  const ajouter = (p: { nom: string; upn: string } | null): void => {
    if (!p || !p.upn) return
    setDestinataires((ds) => (ds.some((d) => memeAdresse(d.upn, p.upn)) ? ds : [...ds, { nom: p.nom, upn: p.upn }]))
    setAjout(false)
  }

  // Échap ferme la fenêtre — et seulement elle (pas la fiche dessous), sauf
  // si c'est la liste de l'annuaire qui est ouverte.
  useEffect(() => {
    function surEchap(e: globalThis.KeyboardEvent): void {
      if (e.key !== 'Escape') return
      if (document.activeElement?.getAttribute('aria-expanded') === 'true') return
      e.stopImmediatePropagation()
      if (!envoi) onClose()
    }
    window.addEventListener('keydown', surEchap, true)
    return () => window.removeEventListener('keydown', surEchap, true)
  }, [envoi, onClose])

  async function envoyer(): Promise<void> {
    if (envoi || manque) return
    // Bascule de rôle : la demande part sous le compte réel.
    if (!confirmerEcriture(`Envoyer ${destination}`)) return
    setEnvoi(true)
    setErreur(null)
    const expediteur = nomReel || user?.nom || ''
    try {
      await deposerEnvoi({
        issueId: issue.id,
        destinataires,
        expediteurUpn: upnReel,
        copieExpediteur,
        contenu: {
          issue,
          equipement: equipement ? { modele: equipement.familleEquipement, chefProjet: equipement.chefProjet } : undefined,
          expediteur,
          note,
          commentaires: retenus,
          autres: candidats.length - retenus.length,
          nbPhotos,
          lien: `${window.location.origin}/issues/${issue.id}`,
          destination,
        },
      })
    } catch (err) {
      console.error('[EnvoiBE] écriture :', err)
      setErreur(messageErreurEnvoi(err))
      setEnvoi(false)
      return
    }
    // La demande est écrite : c'est acquis. Les personnes ajoutées à la main sont retenues
    // (« Vos envois fréquents », au mieux), puis l'activité suit (la fiche dit elle-même si elle échoue).
    void noterDestinataires(upnReel, destinataires.filter((d) => !be.some((b) => memeAdresse(b.upn, d.upn))))
    await onEnvoye(
      texteEvenementEnvoi(destination, destinataires, retenus.length, note),
      `Envoyé ${destination}`,
    )
    setEnvoi(false)
    setEnvoye(destination)
  }

  const surTouche = (e: KeyboardEvent<HTMLDivElement>): void => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault()
      void envoyer()
    }
  }

  // ─── Envoyé ───
  if (envoye) {
    return (
      <Fenetre
        disposition={disposition}
        volet={null}
        titre="Envoyer au BE"
        onReduire={onClose}
        libelleReduire="Fermer"
        pied={<Button variant="primary" fullWidth onClick={onClose}>Fermer</Button>}
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 size={24} color="var(--color-traffic-green)" style={{ flexShrink: 0 }} />
          <div className="flex flex-col gap-1">
            <p style={{ margin: 0, fontWeight: 500 }}>Envoyé {envoye}</p>
            <p style={STYLE_AIDE}>
              Chacun le reçoit d&apos;ici une minute, par mail et / ou Teams selon ses réglages
              {avecExternes ? ' ; les externes, par mail seulement' : ''}. L&apos;envoi est noté dans l&apos;activité de la fiche.
            </p>
          </div>
        </div>
      </Fenetre>
    )
  }

  const pied = (
    <div className="flex flex-col gap-2 w-full">
      <p style={{ ...STYLE_AIDE, color: erreur ? 'var(--color-traffic-red)' : STYLE_AIDE.color }} role="status">
        {erreur ?? manque ?? `${retenus.length > 1 ? `${retenus.length} commentaires` : retenus.length === 1 ? '1 commentaire' : 'Aucun commentaire'} · lien vers la fiche${estBureau ? ' · Ctrl + Entrée pour envoyer' : ''}`}
      </p>
      <div className="flex gap-3">
        {vue === 'ajuster' ? (
          <Button variant="ghost" onClick={() => setVue('express')} disabled={envoi} iconLeft={<ChevronLeft size={16} />}>
            Retour
          </Button>
        ) : (
          <Button variant="ghost" onClick={onClose} disabled={envoi}>Annuler</Button>
        )}
        <Button
          variant="primary"
          fullWidth
          onClick={() => void envoyer()}
          disabled={envoi || manque !== null}
          iconLeft={<Send size={16} />}
        >
          {envoi ? 'Envoi…' : `Envoyer ${destination}`}
        </Button>
      </div>
    </div>
  )

  // ─── Voir et ajuster ───
  if (vue === 'ajuster') {
    const qualifie = issue.criticite !== A_QUALIFIER
    return (
      <Fenetre
        disposition={disposition}
        volet={null}
        titre="Le résumé envoyé"
        sousTitre={`${issue.id} · ${issue.titre}`}
        onReduire={() => setVue('express')}
        libelleReduire="Retour"
        desactive={envoi}
        pied={pied}
      >
        <div className="flex flex-col gap-5" onKeyDown={surTouche}>
          <section className="flex flex-col gap-1">
            <h4 style={STYLE_SECTION}>Toujours envoyé</h4>
            <LigneResume coche="fixe" qui="Description" texte={issue.description || issue.titre} />
            <LigneResume
              coche="fixe"
              qui="Qualification"
              texte={qualifie ? [issue.criticite, issue.famille].filter((v) => v && v !== A_QUALIFIER).join(', ') : 'À qualifier'}
            />
            <LigneResume
              coche="fixe"
              qui="Lien vers la fiche"
              texte={nbPhotos > 0 ? `les ${nbPhotos > 1 ? `${nbPhotos} photos` : 'photo'} s’y voient` : 'Ouvrir dans SCE Issues'}
            />
          </section>

          <section className="flex flex-col gap-1">
            <h4 style={STYLE_SECTION}>Commentaires</h4>
            {candidats.length === 0 && <p style={STYLE_AIDE}>Pas encore de commentaire sur ce problème.</p>}
            {candidats.map((c) => (
              <LigneResume
                key={c.commentaire.id}
                coche={commentairesGardes.has(c.commentaire.id)}
                onBasculer={() => setCommentairesGardes((s) => basculer(s, c.commentaire.id))}
                qui={c.commentaire.auteur}
                texte={tronquer(c.commentaire.contenu, LONGUEUR_EXTRAIT)}
                raison={LIBELLES_RAISON[c.raison]}
                ecartee={!c.garde}
                desactive={envoi}
              />
            ))}
          </section>
        </div>
      </Fenetre>
    )
  }

  // ─── Envoi express ───
  const beRetenus = be.filter((b) => destinataires.some((d) => memeAdresse(d.upn, b.upn)))
  const autres = destinataires.filter((d) => !be.some((b) => memeAdresse(b.upn, d.upn)))
  return (
    <Fenetre
      disposition={disposition}
      volet={null}
      titre={`Envoyer au ${nomBE}`}
      sousTitre={`${issue.id} · ${issue.titre}`}
      onReduire={onClose}
      libelleReduire="Annuler"
      desactive={envoi}
      pied={pied}
    >
      <div className="flex flex-col gap-5" onKeyDown={surTouche}>
        {/* À : le BE du métier, puis les personnes ajoutées. */}
        <div>
          <span style={STYLE_ETIQUETTE}>À</span>
          <div className="flex flex-wrap items-center gap-2">
            {be.length > 0 && (
              <button
                type="button"
                className="envoi-be-chip"
                onClick={() => setBeDeplie((d) => !d)}
                aria-expanded={beDeplie}
                disabled={envoi}
              >
                <span className="envoi-be-groupe" aria-hidden="true">BE</span>
                {nomBE} · {beRetenus.length === be.length ? `${be.length} ${be.length > 1 ? 'personnes' : 'personne'}` : `${beRetenus.length} sur ${be.length}`}
                <ChevronRight size={14} style={{ transform: beDeplie ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s' }} />
              </button>
            )}
            {autres.map((d) => (
              <Etiquette key={d.upn} personne={d} onRetirer={() => retirer(d.upn)} desactive={envoi} />
            ))}
            {!ajout && (
              <button type="button" className="envoi-be-ajout" onClick={() => setAjout(true)} disabled={envoi}>
                + Quelqu&apos;un d&apos;autre
              </button>
            )}
          </div>
          {beDeplie && be.length > 0 && (
            <ul className="flex flex-col gap-1" style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
              {be.map((b) => {
                const retenu = destinataires.some((d) => memeAdresse(d.upn, b.upn))
                return (
                  <li key={b.upn} className="flex items-center gap-2" style={{ fontSize: 14, opacity: retenu ? 1 : 0.5 }}>
                    <PastillePersonne nom={b.nom} upn={b.upn} taille={24} />
                    <span style={{ flex: 1, minWidth: 0 }}>{b.nom}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => (retenu ? retirer(b.upn) : ajouter(b))}
                      disabled={envoi}
                    >
                      {retenu ? 'Retirer' : 'Remettre'}
                    </Button>
                  </li>
                )
              })}
            </ul>
          )}
          {be.length === 0 && (
            <p style={{ ...STYLE_AIDE, marginTop: 6 }}>
              Les personnes du {nomBE} ne sont pas encore réglées dans l&apos;appli : choisissez qui prévenir.
            </p>
          )}
          {ajout && (
            <div style={{ marginTop: 8 }}>
              <ChoixPersonne
                id="envoi-be-personne"
                libelle="Destinataire"
                valeur={{ nom: '' }}
                onChoisir={ajouter}
                autoFocus={be.length > 0}
                invites
                habitues={habituesEnvoi(frequents.filter((f) => adresseProposable(f.upn, rolesCharges()?.externes ?? [])), destinataires)}
                titreHabitues="Vos envois fréquents"
              />
            </div>
          )}
        </div>

        <p style={STYLE_AIDE}>
          Chacun le reçoit par mail et / ou Teams, selon ses réglages « Mes notifications ». Qui a tout coupé reçoit
          quand même le mail.{avecExternes ? ' Les externes, le mail seulement.' : ''}
        </p>

        <div>
          <label htmlFor="envoi-be-note" style={STYLE_ETIQUETTE}>Un mot pour le BE (facultatif)</label>
          <textarea
            id="envoi-be-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            disabled={envoi}
            autoFocus={estBureau}
            placeholder="Ce que vous attendez d’eux : reprendre un plan, valider une modification…"
            className="px-3 py-2 rounded-lg outline-none"
            style={{ ...STYLE_CHAMP, resize: 'vertical' }}
          />
        </div>

        {/* Copie du mail à l'expéditeur : le BE répond au coordinateur dans tous les cas. */}
        <div>
          <label className="flex items-center gap-2" style={{ fontSize: 14, cursor: 'pointer', minHeight: 32 }}>
            <input
              id="envoi-be-copie"
              type="checkbox"
              checked={copieExpediteur}
              onChange={(e) => setCopieExpediteur(e.target.checked)}
              disabled={envoi}
              style={{ width: 18, height: 18, accentColor: 'var(--color-black)' }}
            />
            Me mettre en copie du mail
          </label>
          <p style={{ ...STYLE_AIDE, marginTop: 2 }}>Le BE vous répond directement dans tous les cas.</p>
        </div>

        {/* Le résumé choisi par l'appli, et de quoi l'ajuster. */}
        <div className="envoi-be-resume">
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <Paperclip size={15} />
            <span>
              <b style={{ fontWeight: 600 }}>Le résumé</b> · {retenus.length} {retenus.length > 1 ? 'commentaires' : 'commentaire'}
              {candidats.length > retenus.length ? ` sur ${candidats.length}` : ''} · lien vers la fiche
              {nbPhotos > 0 ? ` (${nbPhotos} ${nbPhotos > 1 ? 'photos' : 'photo'})` : ''}
            </span>
          </div>
          {retenus.length > 0 && (
            <ul>
              {retenus.map((c) => (
                <li key={c.id}>
                  <b>{c.auteur}</b> · {c.contenu}
                </li>
              ))}
            </ul>
          )}
          <div>
            <Button variant="secondary" size="sm" onClick={() => setVue('ajuster')} disabled={envoi} iconRight={<ChevronRight size={14} />}>
              Voir et ajuster
            </Button>
          </div>
        </div>
      </div>
    </Fenetre>
  )
}

// ─── Petites briques ───

function Coche({ coche, fixe }: { coche: boolean; fixe?: boolean }): JSX.Element {
  return (
    <span
      aria-hidden="true"
      className="flex items-center justify-center"
      style={{
        width: 20,
        height: 20,
        borderRadius: 5,
        flexShrink: 0,
        border: coche ? 'none' : '1.5px solid var(--color-gray-50)',
        backgroundColor: fixe ? 'var(--color-gray-20)' : coche ? 'var(--color-black)' : 'var(--color-white)',
        color: fixe ? 'var(--color-black)' : 'var(--color-white)',
        fontSize: 13,
        lineHeight: 1,
      }}
    >
      {coche ? '✓' : ''}
    </span>
  )
}

/** Une ligne de « Voir et ajuster » : la coche, qui, le texte, la raison du choix. */
function LigneResume({
  coche,
  onBasculer,
  qui,
  texte,
  raison,
  ecartee,
  desactive,
}: {
  coche: boolean | 'fixe'
  onBasculer?: () => void
  qui: string
  texte: string
  raison?: string
  /** Écartée d'office par l'appli : l'étiquette le montre en creux. */
  ecartee?: boolean
  desactive?: boolean
}): JSX.Element {
  const fixe = coche === 'fixe'
  const contenu: ReactNode = (
    <>
      <Coche coche={fixe || coche === true} fixe={fixe} />
      <span className="envoi-be-texte" style={{ flex: 1, minWidth: 0, color: fixe || coche ? 'var(--color-black)' : 'var(--color-gray-70)' }}>
        <b style={{ fontWeight: 600 }}>{qui}</b> · {texte}
      </span>
      {raison && <span className="envoi-be-raison" data-ecartee={ecartee}>{raison}</span>}
    </>
  )
  const style: CSSProperties = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '6px 0', fontSize: 14, textAlign: 'left', width: '100%' }
  if (fixe || !onBasculer) return <div style={style}>{contenu}</div>
  return (
    <button
      type="button"
      onClick={onBasculer}
      disabled={desactive}
      aria-pressed={coche === true}
      style={{ ...style, border: 'none', background: 'transparent', cursor: 'pointer', color: 'inherit', fontFamily: 'inherit' }}
    >
      {contenu}
    </button>
  )
}

function Etiquette({ personne, onRetirer, desactive }: { personne: Destinataire; onRetirer: () => void; desactive?: boolean }): JSX.Element {
  return (
    <span className="envoi-be-chip" style={{ cursor: 'default' }}>
      <PastillePersonne nom={personne.nom} upn={personne.upn} taille={22} />
      {personne.nom}
      <button
        type="button"
        onClick={onRetirer}
        disabled={desactive}
        aria-label={`Retirer ${personne.nom}`}
        className="flex items-center justify-center"
        style={{ width: 22, height: 22, border: 'none', background: 'transparent', borderRadius: 11, cursor: 'pointer', color: 'var(--color-gray-70)' }}
      >
        <X size={14} />
      </button>
    </span>
  )
}
